import {
  dismissFoundGuide,
  fetchAdminJobs,
  inviteFoundGuide,
  linkFoundGuide,
  snoozeAdminJobs
} from '@api/adminJobs';
import { hasIncompleteOnboarding, isAutoOpenPath } from '@components/WhatsNew/events';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AdminJobsDialog from './AdminJobsDialog';
import {
  OPEN_ADMIN_JOBS_EVENT,
  isCompanyAdmin,
  markAdminJobs,
  openAdminJobs,
  publishAdminJobsCount
} from './events';

// Where the tie-break job sends the admin: the PIP's Guide Intelligence tab,
// which carries the "Which guide was it?" queue.
export const TIE_BREAK_PATH = '/tipdirect/product-improvement?page=guides';

const CONSOLE_HELP = [
  'Jobs to do:',
  '  tripadmit.jobs()          open the jobs dialog now (even after "Remind me tomorrow")',
  '  tripadmit.jobs.reset()    forget "Remind me tomorrow"; it opens by itself on the next page load',
  'Seed jobs to try it on: node TA-Ticketing/pip/pipeline/guide-jobs.js demo --company <companyId>'
].join('\n');

const installConsoleCommand = () => {
  const jobs = () => {
    openAdminJobs();
    return 'Opening Jobs to do';
  };
  jobs.reset = () =>
    snoozeAdminJobs({ clear: true }).then(() => 'Cleared. Reload the page and the dialog opens by itself.');
  jobs.help = () => console.info(CONSOLE_HELP);
  window.tripadmit = { ...(window.tripadmit || {}), jobs };
  return () => {
    if (window.tripadmit && window.tripadmit.jobs === jobs) delete window.tripadmit.jobs;
  };
};

/**
 * Mounted once in containers/App, beside (and before) the What's new host.
 * Company admins only.
 *
 * - On the first dashboard page after sign-in it asks for the admin's jobs
 *   and opens when there are any, unless they chose "Remind me tomorrow" in
 *   the last day. While it is asking, and while it is open, What's new waits
 *   (events.js: the body attribute).
 * - The top nav "Jobs" button, the PIP's Invite buttons and
 *   `tripadmit.jobs()` open it on demand, snoozed or not.
 * - Closing it with jobs left over is "Remind me tomorrow". Doing a job never
 *   snoozes anything.
 */
export function AdminJobsHost({ user }) {
  const history = useHistory();
  const location = useLocation();
  const userId = user?.userId;
  const enabled = !!userId && isCompanyAdmin(user);
  const onboarding = hasIncompleteOnboarding(user);

  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const [notice, setNotice] = useState(null);
  const autoChecked = useRef(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const show = useCallback(() => {
    setOpen(true);
    markAdminJobs('open');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchAdminJobs();
      setData(result);
      publishAdminJobsCount(result.jobs.length);
      return result;
    } catch (err) {
      setError(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Automatic opening: once per signed-in admin per page load.
  useEffect(() => {
    if (!enabled || onboarding || autoChecked.current === userId || !isAutoOpenPath(location.pathname)) return;
    autoChecked.current = userId;
    markAdminJobs('pending');
    fetchAdminJobs()
      .then(result => {
        setData(result);
        publishAdminJobsCount(result.jobs.length);
        if (result.jobs.length > 0 && !result.snoozedUntil && isAutoOpenPath(pathRef.current)) show();
        else markAdminJobs(null);
      })
      .catch(() => markAdminJobs(null)); // never hold What's new hostage
  }, [enabled, onboarding, userId, location.pathname, show]);

  // On-demand opening.
  useEffect(() => {
    if (!enabled) return undefined;
    const onOpen = event => {
      setFocusId(event.detail?.guideId || null);
      setNotice(null);
      show();
      load();
    };
    window.addEventListener(OPEN_ADMIN_JOBS_EVENT, onOpen);
    const uninstall = installConsoleCommand();
    return () => {
      window.removeEventListener(OPEN_ADMIN_JOBS_EVENT, onOpen);
      uninstall();
    };
  }, [enabled, load, show]);

  // If the host unmounts (sign-out) mid-check, What's new must not wait forever.
  useEffect(() => () => markAdminJobs(null), []);

  const hide = useCallback(() => {
    setOpen(false);
    setNotice(null);
    setFocusId(null);
    markAdminJobs(null);
  }, []);

  // X, Escape, the backdrop and "Remind me tomorrow" all land here.
  const close = useCallback(() => {
    hide();
    if (data?.jobs?.length > 0) snoozeAdminJobs().catch(() => {});
  }, [data, hide]);

  // Each action throws on failure so the form can say why; on success the
  // list is reloaded and the finished job is gone from it.
  const after = async message => {
    setNotice(message);
    await load();
  };

  const onInvite = async (job, { email, tourIds }) => {
    const res = await inviteFoundGuide(job.id, { email, tourIds });
    await after(`Invite sent to ${res.email}. ${job.guide.firstName} will be in Users once they join.`);
  };

  const onLink = async (job, targetId) => {
    await linkFoundGuide(job.id, targetId);
    const target = (data?.knownGuides || []).find(g => g.id === targetId);
    await after(`Linked. Reviews naming ${job.guide.firstName} will count for ${target ? target.name : 'that guide'}.`);
  };

  const onDismiss = async job => {
    await dismissFoundGuide(job.id);
    await after(`Done. We won't suggest ${job.guide.firstName} again.`);
  };

  const onOpenTieBreaks = () => {
    hide();
    history.push(TIE_BREAK_PATH);
  };

  if (!enabled) return null;

  return (
    <AdminJobsDialog
      open={open}
      loading={loading}
      error={error}
      data={data}
      focusId={focusId}
      notice={notice}
      onClose={close}
      onRetry={load}
      onInvite={onInvite}
      onLink={onLink}
      onDismiss={onDismiss}
      onOpenTieBreaks={onOpenTieBreaks}
    />
  );
}

AdminJobsHost.propTypes = {
  user: PropTypes.object
};

export default AdminJobsHost;
